import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useToast } from "@/hooks/use-toast";

export interface PendingInvitation {
  id: string;
  email: string;
  role: "admin" | "member";
  expiresAt: string;
  createdAt: string;
}

export function usePendingInvitations() {
  const { organization, isAdmin } = useOrganization();
  const { toast } = useToast();
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchInvitations = useCallback(async () => {
    if (!organization || !isAdmin) {
      setInvitations([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("organization_invitations")
        .select("*")
        .eq("organization_id", organization.id)
        .is("accepted_at", null)
        .gt("expires_at", new Date().toISOString())
        .order("created_at", { ascending: false });

      if (error) throw error;

      setInvitations(
        (data || []).map((inv) => ({
          id: inv.id,
          email: inv.email,
          role: inv.role as "admin" | "member",
          expiresAt: inv.expires_at,
          createdAt: inv.created_at,
        }))
      );
    } catch (error) {
      console.error("Failed to fetch pending invitations:", error);
    } finally {
      setLoading(false);
    }
  }, [organization, isAdmin]);

  const cancelInvitation = useCallback(async (invitationId: string) => {
    if (!organization || !isAdmin) return false;

    try {
      const { error } = await supabase
        .from("organization_invitations")
        .delete()
        .eq("id", invitationId);

      if (error) throw error;

      toast({
        title: "Invitation cancelled",
        description: "The invitation has been revoked.",
      });

      await fetchInvitations();
      return true;
    } catch (error: any) {
      console.error("Failed to cancel invitation:", error);
      toast({
        title: "Failed to cancel invitation",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
      return false;
    }
  }, [organization, isAdmin, fetchInvitations, toast]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  return {
    invitations,
    loading,
    cancelInvitation,
    refetch: fetchInvitations,
  };
}
